import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDeletedPayrolls, restorePayroll } from "../api/api";
import toast from "react-hot-toast";
import "./Payroll.css";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function getInitials(name = "") {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

const DEPT_COLORS = {
  Software:  { color: "#60a5fa", bg: "#0d1f3c" },
  Marketing: { color: "#f472b6", bg: "#2d0a1e" },
  CEO:       { color: "#facc15", bg: "#1e1500" },
  COO:       { color: "#fb923c", bg: "#1e0e00" },
  HR:        { color: "#a78bfa", bg: "#1a1040" },
  Other:     { color: "#94a3b8", bg: "#1a2540" },
};

export default function DeletedPayrolls() {
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchDeletedPayrolls = async () => {
    try {
      setLoading(true);
      const data = await getDeletedPayrolls();
      const list =
        Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
      setPayrolls(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load deleted payrolls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeletedPayrolls(); }, []);

  const handleRestore = async (payrollId) => {
    try {
      await restorePayroll(payrollId);
      toast.success("Payroll restored successfully");
      setPayrolls(prev => prev.filter(p => p._id !== payrollId));
    } catch (err) {
      toast.error(err?.message || "Failed to restore payroll");
    }
  };

  const enriched = payrolls.map(p => {
    const user = p.userId;
    return {
      ...p,
      displayName:  user?.userName  || user?.name  || "Unknown",
      displayRole:  user?.position  || user?.role  || "—",
      displayDept:  user?.department || "Other",
      displayAvatar: getInitials(user?.userName || user?.name || "?"),
    };
  });

  const filtered = enriched.filter(p =>
    p.displayName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="payroll-loading">Loading...</div>;

  return (
    <div className="payroll-page">
      {/* ── Header ── */}
      <div className="payroll-header">
        <div>
          <h1 className="payroll-title">Deleted Payrolls</h1>
          <p className="payroll-subtitle">Restore deleted payroll records</p>
        </div>
        <div className="payroll-header-actions">
          <button className="add-emp-btn" onClick={() => navigate("/finance")}>← Back to Payroll</button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="payroll-filters">
        <input
          className="payroll-search"
          placeholder="Search employees…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* ── Table ── */}
      <div className="payroll-table-wrap">
        <table className="payroll-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Base Salary</th>
              <th>Absences</th>
              <th>Deductions</th>
              <th>Bonuses</th>
              <th>Net Salary</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const net  = (p.basicSalary || 0) - (p.deduction || 0) - (p.absences || 0) + (p.bonus || 0);
              const dept = DEPT_COLORS[p.displayDept] || { color: "#94a3b8", bg: "#1a2540" };

              return (
                <tr key={p._id} className="emp-row">
                  <td>
                    <div className="emp-cell">
                      <div className="emp-avatar">{p.displayAvatar}</div>
                      <div>
                        <div className="emp-name">{p.displayName}</div>
                        <div className="emp-role">{p.displayRole}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="dept-badge" style={{ color: dept.color, background: dept.bg }}>
                      {p.displayDept}
                    </span>
                  </td>
                  <td className="num-cell">{(p.basicSalary || 0).toLocaleString()} EGP</td>
                  <td>
                    <span className={`abs-badge ${p.absences > 0 ? "has-abs" : ""}`}>
                      {p.absences || 0} days
                    </span>
                  </td>
                  <td className="num-cell red">
                    {(p.deduction || 0) + (p.absences || 0) > 0
                      ? "-" + ((p.deduction || 0) + (p.absences || 0)).toLocaleString() + " EGP"
                      : "—"}
                  </td>
                  <td className="num-cell green">
                    {(p.bonus || 0) > 0 ? "+" + (p.bonus || 0).toLocaleString() + " EGP" : "—"}
                  </td>
                  <td className="num-cell net">{net.toLocaleString()} EGP</td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => handleRestore(p._id)}
                      title="Restore"
                    >↩</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="empty-row">No deleted payrolls found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
